import { memo, useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import { RefreshCw, Search, Users } from 'lucide-react'
import { useJiraPeopleOverview } from './hooks'
import JiraPersonCard from './components/JiraPersonCard'
import JiraOverviewStats from './components/JiraOverviewStats'

const LoadingSkeleton = memo(function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-36 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
        ))}
      </div>
    </div>
  )
})

const ErrorState = memo(function ErrorState({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <Card variant="default" padding="lg" className="text-center">
      <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
        <Users className="h-8 w-8 text-red-600 dark:text-red-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        Failed to Load Data
      </h3>
      <p className="text-gray-600 dark:text-gray-400 mb-4 max-w-md mx-auto">
        {error}
      </p>
      <Button onClick={onRetry} leftIcon={<RefreshCw className="h-4 w-4" />}>
        Retry
      </Button>
    </Card>
  )
})

const JiraPeopleOverview = memo(function JiraPeopleOverview() {
  const { t } = useTranslation()
  const { data, isLoading, error, refresh, lastFetchTime } = useJiraPeopleOverview()
  const [searchQuery, setSearchQuery] = useState('')

  const filteredPeople = useMemo(() => {
    if (!data?.people) return []
    if (!searchQuery.trim()) return data.people

    const query = searchQuery.toLowerCase()
    return data.people.filter(person =>
      person.user.displayName.toLowerCase().includes(query) ||
      person.topProjects.some(p => p.key.toLowerCase().includes(query) || p.name.toLowerCase().includes(query))
    )
  }, [data?.people, searchQuery])

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }, [])

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('jira.title')}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('jira.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {lastFetchTime && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {t('jira.lastUpdated')}: {new Date(lastFetchTime).toLocaleTimeString()}
            </span>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={refresh}
            loading={isLoading}
            leftIcon={<RefreshCw className="h-4 w-4" />}
          >
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {isLoading && !data ? (
        <LoadingSkeleton />
      ) : error ? (
        <ErrorState error={error} onRetry={refresh} />
      ) : data ? (
        <>
          <JiraOverviewStats data={data} />

          <Card variant="default" padding="md">
            <CardHeader compact>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle compact className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary-600" />
                  {t('jira.teamMembers')}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({filteredPeople.length})
                  </span>
                </CardTitle>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearch}
                    placeholder={t('jira.searchPlaceholder')}
                    className="pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full sm:w-64"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent compact>
              {filteredPeople.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredPeople.map(person => (
                    <JiraPersonCard key={person.user.accountId} person={person} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">{t('jira.noResults')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  )
})

export default JiraPeopleOverview

